import itemsTemplate from '../admin/itemAdmin/dom/items/items.template.html';
import itemCategoriesTemplate from '../admin/itemAdmin/dom/itemCategories/itemCategories.template.html';
import itemOptionsTemplate from '../admin/itemAdmin/dom/itemOptions/itemOptions.template.html';

/*******************************************************************************
* @ngdoc       provider
* @name        ItemAdminServiceProvider
* @module      csm.books.ItemAdmin
* @description 
* Provides configuration of the ItemAdminService. A utility service to retrieve 
* and maintain lists used by Item Admin and the AddChangeItemDialog. This must
* be configured in the application using this module so that the routes will be
* effective on for that application.
* 
* This utility is created as a provider so the main app running can set up the
* routes conditionally using 'ItemAdminServiceProvider.configureRoutes'.
* 
* The singleton that is created will contain a means to load all necessary item
* data and cache the values for multiple component access.
*******************************************************************************/
/* @ngInject */ export function itemAdminServiceProvider($injector) {
  this.configureRoutes = $injector.invoke(configureRoutes, this);
  this.$get = $injector.invoke(service, this);
  this.httpServiceBase = '/books/itemAdmin/';
  this.httpServices = {
    dropItem: function (type, id) {
      return this.get('dropItem/{0}/{1}'.format(type, id));
    },
    dropItemOption: function (id) {
      return this.get('dropItemOption/{0}'.format(id));
    },
    dropItemOptionMaster: function (id) {
      return this.get('dropItemOptionMaster/{0}'.format(id));
    },
    getItemAdminInfo: function () {
      return this.get('getItemAdminInfo');
    },
    getItemAdminDialogInfo: function (type, id) {
      return this.get('getItemAdminDialogInfo/{0}/{1}'.format(type, id));
    },
    saveItem: function (data) {
      return this.post('saveItem', data);
    }
  };
}

/*******************************************************************************
 * provider.configureRoutes */
/**
 * Utility function that is used to configure angular routing to work with the
 * itemAdmin directive app. This needs to be called in the using app otherwise
 * if this was done as a normal module.config then it would apply across any
 * app using CSMBooksWeb.
 *******************************************************************************/
/* @ngInject */ function configureRoutes($routeProvider) {
  return function () {
    /*--------------------------------------------------*/
    /* Upon injection, ensure that the lists are loaded */
    /* before the directive controller is instantiated. */
    /*--------------------------------------------------*/
    var resolve = {
      init: function (ItemAdminService) {
        return ItemAdminService.init();
      }
    };

    $routeProvider
      .when('/', {
        template: itemsTemplate,
        controller: 'itemsController',
        resolve: resolve
      })
      .when('/categories/', {
        template: itemCategoriesTemplate,
        controller: 'itemCategoriesController',
        resolve: resolve
      })
      .when('/options/', {
        template: itemOptionsTemplate,
        controller: 'itemOptionsController',
        resolve: resolve
      })
      .otherwise({ redirectTo: '/' });
  };
}

/*******************************************************************************
 * service */
/**
 * @ngdoc       service
 * @name        ItemAdminService
 * @module      csm.books.ItemAdmin
 * @description
 *******************************************************************************/
/* @ngInject */ function service() {
  return function ($q, BooksSvc, BusyScreen, HTTPService) {
    var initialized = false;

    /*----------------*/
    /* Dataproviders. */
    /*----------------*/
    var mItems = [];
    var mCategories = [];
    var mOptions = [];

    /*----------*/
    /* Getters. */
    /*----------*/
    var getItems = function () {
      return mItems;
    };
    var getCategories = function () {
      return mCategories;
    };
    var getOptions = function () {
      return mOptions;
    };

    /*----------------------*/
    /* Reload list methods. */
    /*----------------------*/
    var reloadItems = function () {
      return BooksSvc.getItemList(null, null, null, null).then(
        function (resp) {
          mItems = resp.data;
        }
      );
    };
    var reloadCategories = function () {
      return BooksSvc.getItemCategories().then(function (resp) {
        mCategories = resp.data;
      });
    };
    var reloadOptions = function () {
      return BooksSvc.getItemOptionMasterList().then(function (resp) {
        mOptions = resp.data;
      });
    };

    /*-------------------------------------------------------------*/
    /* init - Initialize the server and return a resolved promise. */
    /*-------------------------------------------------------------*/
    var init = function () {
      var promise = initialized
        ? $q.resolve()
        : this.getItemAdminInfo().then(function (resp) {
            mItems = resp.items;
            mOptions = resp.optionMasterList;
            mCategories = resp.itemCategories.sort(function (a, b) {
              return a.category
                .toLowerCase()
                .localeCompare(b.category.toLowerCase());
            });
            initialized = true;
            BusyScreen.setBusy(false);
          });

      return promise;
    };

    /*---------------------------------------------*/
    /* Return an API to interact with the service. */
    /*---------------------------------------------*/
    return Object.assign(
      HTTPService.make(this.httpServiceBase),
      this.httpServices,
      {
        init: init,
        getItems: getItems,
        getCategories: getCategories,
        getOptions: getOptions,
        reloadItems: reloadItems,
        reloadCategories: reloadCategories,
        reloadOptions: reloadOptions
      }
    );
  };
}
